/**
 * 착수계 API — backend /commencement/* 와 1:1.
 *
 * 분할납품요구서(분납서) PDF 업로드 → 9종 양식 PDF 생성.
 * 분납서 자동추출·문서번호 제안은 납품계 API(parseBunapFile·getNextDocNo) 재사용.
 */

import type { ApiResponse } from './types';
import { appendAttachmentsToForm, type DocExtraFile } from './delivery-api';

const BASE = '/api';

export interface CommencementListRow {
  id: number;
  docNo: string;
  issueDate: string;
  recipient: string;
  businessName: string;
  amount: number;
  bunapFilename: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** 표지 붙임서류 항목 = 문서이름 + 제출부수 (+ 항목별 첨부 PDF) */
export interface DocAttachment {
  name: string;
  copies: string;
  file?: File | null;
  existingFileId?: number | null;
  existingFileName?: string | null;
}

export interface CommencementDetail {
  id: number;
  docNo: string;
  issueDate: string;
  recipient: string;
  department: string | null;
  businessName: string;
  procurementNo: string | null;
  contractDate: string | null;
  deliveryDue: string | null;
  amount: number;
  itemName: string | null;
  spec: string | null;
  identNo: string | null;
  unit: string | null;
  qty: number;
  linkedSalesId: string | null;
  includeSeal: boolean;
  attachments: DocAttachment[] | null;
  extraFiles: DocExtraFile[];
  bunapFilename: string | null;
  createdBy: string | null;
}

export interface CommencementCreateInput {
  docNo?: string;
  issueDate: string;      // 시행일자 = 착수년월일
  recipient: string;
  department?: string;
  businessName: string;
  procurementNo?: string;
  contractDate?: string;  // 계약년월일(=납품요구일자)
  deliveryDue?: string;   // 납품기한 = 준공년월일 (필수 — 예정공정표 계산)
  amount: number;
  itemName?: string;
  spec?: string;
  identNo?: string;       // 물품식별번호
  unit?: string;
  qty?: number;
  includeSeal?: boolean;
  attachments?: DocAttachment[]; // 붙임서류 목록 (미지정이면 기본 목록)
}

export type CommencementUpdateInput = Partial<CommencementCreateInput>;

async function dataOf<T>(res: Response): Promise<T> {
  const json = (await res.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function listCommencements(): Promise<CommencementListRow[]> {
  const res = await fetch(`${BASE}/commencement`, { credentials: 'include' });
  return (await dataOf<{ items: CommencementListRow[] }>(res)).items;
}

export async function getCommencement(id: number): Promise<CommencementDetail> {
  const res = await fetch(`${BASE}/commencement/${id}`, { credentials: 'include' });
  return dataOf<CommencementDetail>(res);
}

/** 착수계 작성 — 필드 + 분납서(필수) */
export async function createCommencement(
  input: CommencementCreateInput,
  bunap: File,
): Promise<{ id: number; docNo: string }> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || k === 'attachments') continue;
    fd.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
  }
  appendAttachmentsToForm(fd, input.attachments);
  fd.append('bunap', bunap);
  const res = await fetch(`${BASE}/commencement`, { method: 'POST', credentials: 'include', body: fd });
  return dataOf(res);
}

/** 착수계 수정 — 필드(보낸 것만 갱신) */
export async function updateCommencement(id: number, input: CommencementUpdateInput): Promise<void> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || k === 'attachments') continue;
    fd.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
  }
  appendAttachmentsToForm(fd, input.attachments);
  const res = await fetch(`${BASE}/commencement/${id}`, { method: 'PATCH', credentials: 'include', body: fd });
  await dataOf(res);
}

export async function deleteCommencement(id: number): Promise<void> {
  const res = await fetch(`${BASE}/commencement/${id}`, { method: 'DELETE', credentials: 'include' });
  await dataOf(res);
}

export function commencementPdfUrl(id: number): string {
  return `${BASE}/commencement/${id}/pdf`;
}
